'use client';

import type { ReactNode } from 'react';
import cs from 'classnames';
import { styled } from 'styled-components';
import { InView } from 'react-intersection-observer';
import * as Styled from '@lib/styles';
import TextLink from '@dvd/components/TextLink';
import Media from '@dvd/components/Media';
import type { ReactNodes, CtaProps, ImageProps } from '@types';

/* TODO: refactor when comes time to better use BaseText */
type ScrollingChildProps = {
  /**
   * What the block headline says
   */
  headline: string;
  /**
   * Descriptive paragraph
   * - Use a simple string (e.g. "A sentence of words.") for straight copy.
   * - Use HTML to apply simple formatting (e.g. A sentence with an <strong>important</strong> word.)
   */
  description: ReactNode;
  /**
   * The hashed ID of the video to display
   * - **Required** for video content (mutually exclusive with image)
   */
  hashedId?: string;
  /**
   * Featured image to display
   * - **Required** for image content (mutually exclusive with hashedId for video content)
   * - Only `src` and `alt` properties are required; `width` and `height` are fixed to 528x583 for layout consistency
   */
  image?: Pick<ImageProps, 'src' | 'alt'>;
  /**
   * Optional link for the block
   */
  cta?: CtaProps;
};

const BlockContent = styled.div`
  h2,
  p {
    margin-block-end: ${({ theme }) => theme.spacing[0.75]};
  }

  /** add some extra breathing room before the next block on mobile */
  padding-block-end: ${({ theme }) => theme.spacing[4]};

  /** Minimize breathing room on first one on mobile */
  &:first-child {
    padding-block-end: ${({ theme }) => theme.spacing[1]};
  }
  ${({ theme }) => theme.mq.minWidth.medium} {
    padding-block-end: 0;

    &:first-child {
      padding-block-end: 0;
    }
  }
`;

// Note: used to attach progressive enancement styles for the medium-screen+ scrolling effect
const BlockMedia = styled.div`
  & > div:first-child {
    margin: 0;
  }

  ${({ theme }) => theme.mq.minWidth.medium} {
    padding-block-start: ${({ theme }) => theme.spacing[2]};
  }
`;

/**
 *
 * The layout of `ScrollingChild` is largely styled in its parent module, `Scrolling`.
 * This component is simply to demonstrate how props are used.
 *
 * Rules:
 * - Only to be used as child of the `Scrolling` module
 * - `headline` and `description` are required
 * - `hashedId` is required for video content; mutually exclusive with image props
 * - `src` and `alt` are required for image content; width and height are fixed to 528x583 for layout consistency
 * - If `hashedId` is provided, src and alt properties are ignored
 */
export const ScrollingChild = ({
  headline,
  description,
  cta = null,
  image,
  hashedId,
}: ScrollingChildProps) => {
  return (
    <InView threshold={0.8}>
      {({ inView, ref }) => (
        <>
          <BlockContent ref={ref}>
            <h3>{headline}</h3>
            <p>{description}</p>
            {cta?.href ? <TextLink cta={cta} /> : null}
          </BlockContent>
          <BlockMedia className={cs({ active: inView })}>
            {hashedId ? (
              <Media hashedId={hashedId} />
            ) : (
              <Media alt={image.alt} height={583} src={image.src} width={528} />
            )}
          </BlockMedia>
        </>
      )}
    </InView>
  );
};

/* TODO: refactor when comes time to better use BaseText */
type ScrollingProps = {
  /**
   * What the introductory headline says
   */
  headline: string;
  /**
   * Descriptive paragraph introducing the scrolling slides
   */
  description?: string;
  /**
   * Optional cta for the module
   */
  cta?: CtaProps;
  /**
   * Three or four ScrollingChild(ren)
   */
  children: ReactNodes<3> | ReactNodes<4>;
  /**
   * Optional class name, nesc. for extending as a styled-component
   */
  className?: string;
};

/* TODO: create a function for max-width value. It's derived from 8 columns in the 12 column grid */
const ScrollingMessaging = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1.5]};
  margin-inline: auto;
  max-width: 54rem;
  text-align: center;

  h2,
  p {
    margin: 0;
  }
`;

const ScrollingBlocks = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: ${({ theme }) => theme.spacing[2]};

  /** Note: we want to switch to grid view on tablet, but use the column structure of the L & XL */
  ${({ theme }) => theme.mq.minWidth.medium} {
    align-items: center;
    display: grid;
    flex-direction: column;
    ${({ theme }) => theme.grid.columns.large}
    grid-auto-rows: 1fr;
    row-gap: ${({ theme }) => theme.spacing[10]};

    ${BlockContent} {
      grid-column: 2 / span 4;
      padding-block: ${({ theme }) => theme.spacing[10]};
    }
    ${BlockMedia} {
      grid-column: 7 / span 5;
      grid-row: 1;
      opacity: 0;
      position: sticky;
      top: calc(25% - ${({ theme }) => theme.spacing[4]});
      transition: opacity 333ms ease-in-out;

      &.active {
        opacity: 1;
      }
    }
  }
`;

const ScrollingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-block: ${({ theme }) => theme.spacing[2]};
`;

/**
 * Scrolling module with automatic visual transitions for medium screens (721px and up). It displays a series of sticky text with content (image or video) that scrolls as the user navigates up or down on the page.
 * - To see the scrolling behavior, use Storybook's toolbar in `Default` story to open the canvas in a new tab.
 *
 * Rules:
 * - Content should be objects using `ScrollingChildProps` that include media props (video/image) and content props (headline/description).
 * - Content should include a minimum of two set of media and content props.
 *
 * *Note:* Practically speaking, should be lower on the page. But technically speaking, no
 * guard-rails against other usage.
 */
const Scrolling = ({
  headline,
  description,
  cta,
  children,
}: ScrollingProps) => {
  return (
    <ScrollingContainer>
      <ScrollingMessaging>
        <h2>{headline}</h2>
        {description ? <Styled.BodyLg>{description}</Styled.BodyLg> : null}
        {cta?.href ? <TextLink cta={cta} /> : null}
      </ScrollingMessaging>
      <ScrollingBlocks>{children}</ScrollingBlocks>
    </ScrollingContainer>
  );
};

export default Scrolling;
